/* eslint-disable react/jsx-max-depth */
import { useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import EditIcon from '@mui/icons-material/Edit';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import HubIcon from '@mui/icons-material/Hub';
import MuiLink from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { ensureDefined } from '@e-socna/utils';
import {
  drillFragmentData,
  getFragmentData,
  graphql,
  MissionKindEnum,
} from '@e-socna/data-layer';
import { EMPTY_ARRAY } from '@e-socna/empty';
import type { MissionWorkflowId } from '@e-socna/data-layer';

import { useMissionAccess } from '../../components/Access/hooks/useMissionAccess.hook';
import { Memo } from '../../components/Memo';
import { RightDrawer } from '../../components/RightDrawer';
import { MissionForm } from '../../forms/Mission';
import { FormVariant } from '../../forms';
import { MissionFormTab } from '../../forms/Mission/enums/tab.enum';
import { SpeedDial } from '../../components/SpeedDial';
import { Code } from '../../components/Code';
import { Avatar } from '../../components/Avatar';
import { ThirdPartyName } from '../../components/ThirdPartyName';
import { formatDateTime } from '../../utils/format-date-time.util';
import { MissionCategoryPath } from '../../components/MissionCategoryPath';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { MissionStateForm } from '../../forms/MissionState';
import { useMissionReadyContext } from '../../contexts/MissionContext/hooks/useMissionReadyContext.hook';
import { PATH_MISSION_DEFAULT } from '../../app/constants/paths.constant';

import { MissionPage_Tabs_Mission_Fragment } from './Tabs';
import { buildMissionPath } from '../../app/utils/mission-space.util';
import { missionKindProfileOf } from '../../app/utils/mission-kind-profile.util';
import { InfoAlert } from '../../components/InfoAlert';

export const MissionPage_GeneralInfo_Mission_Fragment = graphql(`
  fragment MissionPage_GeneralInfo_Mission_Fragment on Mission {
    kind
    _id
    codingConfig {
      kind
      strategyId
      ... on CodingConfigManual {
        code
      }
      ...CodeCmp_CodingConfig_Fragment
    }
    code
    businessCreationDate
    businessCreatorId
    color
    stateId
    workflowId
    isConfidential
    internalMemo
    memo
    typeId
    categoryParentId
    categoryLeaf
    folderId

    compositeMission {
      _id
      code
      consolidation {
        title
      }
    }

    consolidation {
      title
      description
      isTitleFallback
      isDescriptionFallback
    }

    ...MissionForm_Mission_Fragment
  }
`);

interface Props {
  readonly paths: Readonly<{
    consult: string;
    editGeneralInfo: string;
    editCode: string;
    editCollaboration: string;
    editState: string;
  }>;
}

export const GeneralInfo = ({ paths }: Readonly<Props>) => {
  const {
    mission: rawMission,
    folder,
    resolvableMissionType,
  } = useMissionReadyContext();

  const tabsMission = getFragmentData(
    MissionPage_Tabs_Mission_Fragment,
    rawMission,
  );
  const mission = drillFragmentData(
    [
      MissionPage_Tabs_Mission_Fragment,
      MissionPage_GeneralInfo_Mission_Fragment,
    ],
    rawMission,
  );
  const { wording } = missionKindProfileOf(mission.kind);

  const { missionWorkflowsIdMap } = useReferencesContext();

  const { pathname: currentPath } = useLocation();
  const navigate = useNavigate();

  const isInGeneralInfoEditionMode = currentPath === paths.editGeneralInfo;
  const isInCodeEditionMode = currentPath === paths.editCode;
  const isInCollaborationEditionMode = currentPath === paths.editCollaboration;
  const isInStateEditionMode = currentPath === paths.editState;
  const isInEditionMode =
    isInGeneralInfoEditionMode ||
    isInCodeEditionMode ||
    isInCollaborationEditionMode;
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  const enterStateEditionMode = useCallback(
    () => void navigate(paths.editState),
    [navigate, paths.editState],
  );

  const { missionWorkflow } = ensureDefined({
    missionWorkflow:
      missionWorkflowsIdMap[mission.workflowId as MissionWorkflowId],
  });

  const access = useMissionAccess(mission._id, mission);

  return (
    <>
      <Stack spacing={2} sx={{ pt: 4, width: '100%' }}>
        <MissionStateForm
          enterEditionMode={enterStateEditionMode}
          isInEditionMode={isInStateEditionMode}
          leaveEditionMode={leaveEditionMode}
          mission={tabsMission}
        />
        <InfoAlert>
          {mission.consolidation.isTitleFallback ||
          mission.consolidation.isDescriptionFallback
            ? 'Le titre ainsi que la description sont pour le moment hérités.'
            : 'Le titre ainsi que la description peuvent être hérités.'}
        </InfoAlert>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <Typography
              color="text.secondary"
              gutterBottom
              sx={{ display: 'block', lineHeight: 1.2 }}
              variant="overline"
            >
              Catégorie :{' '}
              <MissionCategoryPath
                categoryLeaf={mission.categoryLeaf}
                categoryParentId={mission.categoryParentId}
              />
              {!!resolvableMissionType && (
                <>
                  <br />
                  Type : {resolvableMissionType.label}
                </>
              )}
            </Typography>
            <Typography gutterBottom variant="h6">
              {mission.consolidation.title}
            </Typography>
            <Typography
              gutterBottom
              sx={{ whiteSpace: 'pre-line' }}
              variant="body1"
            >
              {mission.consolidation.description
                ? mission.consolidation.description
                : 'Aucune description renseignée'}
            </Typography>
            <Avatar
              bgColor={mission.color}
              icon={<BusinessCenterIcon />}
              label={`Avatar ${wording.ofName}`}
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
              }}
            />
          </Box>
        </Paper>
        <Memo
          content={{
            internal: mission.internalMemo,
            memo: mission.memo,
          }}
          display={{ internal: true, memo: true }}
        />
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <Code code={mission.code} codingConfig={mission.codingConfig} />
            <Typography gutterBottom variant="body1">
              Date de création : {formatDateTime(mission.businessCreationDate)}
            </Typography>
            <Typography gutterBottom variant="body1">
              Créateur : <ThirdPartyName id={mission.businessCreatorId} />
            </Typography>
            <Typography gutterBottom variant="body1">
              Workflow : {missionWorkflow.label}
            </Typography>
            {mission.compositeMission ? (
              <Typography gutterBottom variant="body1">
                Collaboration :{' '}
                <MuiLink
                  component={Link}
                  to={buildMissionPath(PATH_MISSION_DEFAULT, {
                    folderId: mission.folderId,
                    missionId: mission.compositeMission._id,
                    kind: MissionKindEnum.Composite,
                  })}
                >
                  {mission.compositeMission.code}
                </MuiLink>{' '}
                - {mission.compositeMission.consolidation.title}
              </Typography>
            ) : null}
            <Typography gutterBottom variant="body1">
              Confidentielle : {mission.isConfidential ? 'Oui' : 'Non'}
            </Typography>
          </Box>
        </Paper>
      </Stack>
      <SpeedDial
        actions={[
          {
            id: 'editMissionGeneralInfo',
            gate: access.update,
            icon: <EditIcon />,
            name: 'Modifier Infos Générales',
            to: paths.editGeneralInfo,
          },
          {
            id: 'editMissionCode',
            gate: access.update,
            icon: <FingerprintIcon />,
            name: 'Modifier Code',
            to: paths.editCode,
          },
          ...(!missionKindProfileOf(mission.kind).canChangeItsCollaboration
            ? []
            : [
                {
                  id: 'editMissionCollaboration',
                  gate: access.updateCollaborationLink,
                  icon: <HubIcon />,
                  name: 'Modifier Collaboration',
                  to: paths.editCollaboration,
                },
              ]),
        ]}
        title={`Actions possibles sur les Informations Générales ${wording.ofName}`}
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <MissionForm
          folderConsolidation={folder.consolidation}
          formVariant={FormVariant.Modification}
          initialValues={mission}
          missionId={mission._id}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          stepIdsSubset={
            isInGeneralInfoEditionMode
              ? [MissionFormTab.GeneralInfo]
              : isInCodeEditionMode
                ? [MissionFormTab.CodingConfig]
                : isInCollaborationEditionMode
                  ? [MissionFormTab.Collaboration]
                  : EMPTY_ARRAY
          }
          title={
            isInGeneralInfoEditionMode
              ? `Modifier les Informations Générales ${wording.ofQualifiedName}`
              : isInCodeEditionMode
                ? `Modifier le Code ${wording.ofQualifiedName}`
                : isInCollaborationEditionMode
                  ? `Modifier la Collaboration ${wording.ofQualifiedName}`
                  : `Modifier la ${wording.qualifiedName}`
          }
        />
      </RightDrawer>
    </>
  );
};
